// ABOUTME: Applies page overrides to the live DOM (text, styles, mobile styles, order, visibility).
// ABOUTME: Handlers are keyed by patch kind so future kinds (insert/replace) can be added without changes here.

import { isEditorNode, resolveSelector, selectorKey, type PageOverride, type StylePatch } from './types';

const MOBILE_STYLE_ID = 'page-overrides-mobile';
const APPLIED_ATTR = 'data-po-applied';

export interface ApplyResult {
  /** Selectors that resolved and were applied */
  applied: string[];
  /** Selectors that no longer exist in the markup — silently skipped for visitors */
  missing: string[];
}

const cssText = (style: Record<string, string>, important = false) =>
  Object.entries(style)
    .filter(([, v]) => v !== '' && v != null)
    .map(([k, v]) => `${k}:${v}${important ? ' !important' : ''};`)
    .join('');

const rememberOriginals = (el: HTMLElement) => {
  if (el.hasAttribute(APPLIED_ATTR)) return;
  el.setAttribute(APPLIED_ATTR, 'true');
  el.dataset.poOrigStyle = el.getAttribute('style') ?? '';
  el.dataset.poOrigText = el.textContent ?? '';
  const parent = el.parentElement;
  if (parent) el.dataset.poOrigIndex = String(Array.from(parent.children).indexOf(el));
};

/** Restores every element previously touched by an override. */
export const resetOverrides = (root: ParentNode = document) => {
  const touched = Array.from(root.querySelectorAll<HTMLElement>(`[${APPLIED_ATTR}]`));
  // Restore order first so text/style restoration is unaffected by moves
  touched
    .filter((el) => el.dataset.poOrigIndex !== undefined)
    .sort((a, b) => Number(a.dataset.poOrigIndex) - Number(b.dataset.poOrigIndex))
    .forEach((el) => {
      const parent = el.parentElement;
      if (!parent) return;
      const index = Number(el.dataset.poOrigIndex);
      const at = parent.children[index];
      if (at && at !== el) parent.insertBefore(el, at);
    });

  touched.forEach((el) => {
    const origStyle = el.dataset.poOrigStyle ?? '';
    if (origStyle) el.setAttribute('style', origStyle);
    else el.removeAttribute('style');
    if (el.dataset.poTextApplied === 'true' && el.dataset.poOrigText !== undefined) {
      el.textContent = el.dataset.poOrigText;
    }
    el.removeAttribute(APPLIED_ATTR);
    delete el.dataset.poOrigStyle;
    delete el.dataset.poOrigText;
    delete el.dataset.poOrigIndex;
    delete el.dataset.poTextApplied;
    delete el.dataset.poKey;
  });

  document.getElementById(MOBILE_STYLE_ID)?.remove();
};

const applyStylePatch = (el: HTMLElement, patch: StylePatch, mobileRules: string[]) => {
  rememberOriginals(el);

  if (typeof patch.text === 'string' && patch.text !== '') {
    el.textContent = patch.text;
    el.dataset.poTextApplied = 'true';
  }

  if (patch.style) el.style.cssText += cssText(patch.style);
  if (patch.hidden) el.style.display = 'none';

  if (patch.styleMobile && Object.keys(patch.styleMobile).length > 0) {
    const key = selectorKey(el.dataset.poKey || `${el.tagName}${mobileRules.length}`);
    el.dataset.poKey = key;
    mobileRules.push(`[data-po-key="${key}"]{${cssText(patch.styleMobile, true)}}`);
  }

  if (typeof patch.order === 'number' && el.parentElement) {
    const parent = el.parentElement;
    const target = parent.children[patch.order];
    if (target && target !== el) parent.insertBefore(el, target);
    else if (!target) parent.appendChild(el);
  }
};

/** Handlers keyed by patch kind — unknown kinds are silently ignored. */
const HANDLERS: Record<string, (el: HTMLElement, o: PageOverride, mobileRules: string[]) => void> = {
  style: (el, o, mobileRules) => applyStylePatch(el, o.patch as StylePatch, mobileRules),
};

/**
 * Applies overrides for the current page. Never throws and never renders any
 * user-visible error: overrides whose selector no longer resolves are skipped.
 */
export const applyOverrides = (overrides: PageOverride[], root: ParentNode = document): ApplyResult => {
  resetOverrides(root);

  const applied: string[] = [];
  const missing: string[] = [];
  const mobileRules: string[] = [];

  [...overrides]
    .sort((a, b) => a.sort_order - b.sort_order)
    .forEach((override) => {
      const handler = HANDLERS[override.kind];
      if (!handler) return;
      const el = resolveSelector(override.selector, root);
      if (!el || !(el instanceof HTMLElement) || isEditorNode(el)) {
        missing.push(override.selector);
        return;
      }
      try {
        handler(el, override, mobileRules);
        applied.push(override.selector);
      } catch {
        missing.push(override.selector);
      }
    });

  if (mobileRules.length > 0) {
    const style = document.createElement('style');
    style.id = MOBILE_STYLE_ID;
    style.textContent = `@media (max-width: 640px){${mobileRules.join('')}}`;
    document.head.appendChild(style);
  }

  return { applied, missing };
};
